import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TablePagination,
  Chip,
  IconButton,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from "@mui/icons-material";

const FdpUpcoming = ({ events = [] }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedEvents = events.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDomainColor = (domain) => {
    if (!domain) return "#9E9E9E";
    const colors = [
      "#4CAF50", "#2196F3", "#FF9800", "#9C27B0",
      "#F44336", "#00BCD4", "#FF5722", "#3F51B5",
    ];
    const index =
      domain.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return colors[index];
  };

  const getEventTypeColor = (type) => {
    return type === "FDP"
      ? { color: "#2196F3", bgColor: "#E3F2FD" }
      : { color: "#FF9800", bgColor: "#FFF3E0" };
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          mb: 2,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#F8FAFC",
                borderBottom: "1px solid #D1D5DB",
              }}
            >
              {/* Domain comes first for Upcoming */}
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Domain
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Event Type
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Start Date
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  End Date
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Trainer Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  Trainer Email
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEvents.map((event, index) => {
              const eventTypeColor = getEventTypeColor(event.event_type);
              return (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#F9FAFB" },
                    "& td": {
                      borderBottom:
                        index === paginatedEvents.length - 1
                          ? "none"
                          : "1px solid #E5E7EB",
                    },
                  }}
                >
                  {/* Domain first */}
                  <TableCell
                    sx={{ borderRight: "1px solid #E5E7EB" }}
                    title={event.event_domain_name}
                  >
                    <Chip
                      label={truncateText(event.event_domain_name, 30)}
                      sx={{
                        backgroundColor: `${getDomainColor(
                          event.event_domain_name
                        )}15`,
                        color: getDomainColor(event.event_domain_name),
                        fontWeight: 500,
                        borderRadius: "8px",
                        fontSize: "0.7rem",
                        height: "24px",
                        maxWidth: "250px",
                      }}
                      size="small"
                    />
                  </TableCell>
                  {/* Event Type second */}
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Chip
                      label={event.event_type || "-"}
                      sx={{
                        backgroundColor: eventTypeColor.bgColor,
                        color: eventTypeColor.color,
                        fontWeight: 600,
                        borderRadius: "8px",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {formatDate(event.start_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {formatDate(event.end_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: "1px solid #E5E7EB" }}
                    title={event.fullname}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <PersonIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#1F2937", fontWeight: 500 }}
                      >
                        {truncateText(event.fullname, 25)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell title={event.email}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <EmailIcon
                        sx={{ fontSize: "0.9rem", color: "#6B7280" }}
                      />
                      <Typography variant="body2" sx={{ color: "#4B5563" }}>
                        {truncateText(event.email, 30)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {paginatedEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    No upcoming events found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper
        sx={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          borderRadius: "12px",
        }}
      >
        <TablePagination
          component="div"
          count={events.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              color: "#6B7280",
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default FdpUpcoming;
